import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import api from "../lib/api";
import { PASSWORD_MIN_LEN as MIN_LEN, formatApiErrorDetail } from "../lib/password";

// Authenticated self-service password change (forge-20260615-001).
//
// Reused by the partner console header and the member dashboard chrome so
// a logged-in user of any role can rotate their password in-app via
// POST /auth/change-password. The min-length + error formatter come from
// lib/password so this flow can't drift from ResetPasswordPage; the backend
// is the source of truth.

/**
 * Self-contained Change-password modal. The trigger is rendered by the
 * caller; this component owns only the dialog open state and form.
 *
 * Props:
 *   - triggerClassName: optional class for the built-in trigger button
 *   - triggerVariant / triggerSize: Button props for the trigger
 *   - triggerLabel: trigger text (default "Change password")
 */
const ChangePasswordDialog = ({
  triggerClassName = "",
  triggerVariant = "outline",
  triggerSize = "sm",
  triggerLabel = "Change password",
}) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setSubmitting(false);
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v) reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < MIN_LEN) {
      toast.error(`New password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    if (next === current) {
      toast.error("New password must be different from your current password.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Your password has been updated.");
      onOpenChange(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Failed to change password."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Button
        type="button"
        variant={triggerVariant}
        size={triggerSize}
        className={triggerClassName}
        onClick={() => setOpen(true)}
        data-testid="change-password-trigger"
      >
        <KeyRound size={14} className="mr-1.5" />
        {triggerLabel}
      </Button>

      <DialogContent className="bg-white sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Change password</DialogTitle>
          <DialogDescription className="text-slate-600">
            Enter your current password and choose a new one (minimum {MIN_LEN}{" "}
            characters).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cp-current" className="text-slate-700">
              Current password
            </Label>
            <Input
              id="cp-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="bg-white border-slate-200 text-slate-900"
              autoComplete="current-password"
              required
              data-testid="change-password-current"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cp-new" className="text-slate-700">
              New password
            </Label>
            <Input
              id="cp-new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="bg-white border-slate-200 text-slate-900"
              minLength={MIN_LEN}
              autoComplete="new-password"
              required
              data-testid="change-password-new"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cp-confirm" className="text-slate-700">
              Confirm new password
            </Label>
            <Input
              id="cp-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-white border-slate-200 text-slate-900"
              minLength={MIN_LEN}
              autoComplete="new-password"
              required
              data-testid="change-password-confirm"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="change-password-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="change-password-submit"
            >
              {submitting ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
